/**
 * File validation utilities for media uploads
 */
import { extensionsForMimeTypes } from './constants';

/**
 * What a browser reports when it cannot identify a file. Not an edge case: with
 * no OS-level mapping for the extension, Chrome on some Windows/Linux setups
 * and several Android pickers send one of these for a valid PDF (BAL-747).
 */
const GENERIC_BINARY_MIME_TYPES = [
  'application/octet-stream',
  'binary/octet-stream',
  '',
];

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export interface FileValidationOptions {
  allowedTypes: string[];
  maxFileSize: number;
  /**
   * Accept a file whose browser-reported MIME is generic (or absent) when its
   * extension matches one of `allowedTypes`. See {@link isAllowedFileType}.
   *
   * Off by default, and deliberately opt-in per caller: this gate must never be
   * more permissive than the server action behind it, or the user picks a file
   * the client accepted and the server then refuses. Turn it on only where the
   * receiving action applies the same fallback — today the dancer-media
   * document path (BAL-747).
   */
  allowExtensionFallback?: boolean;
}

/**
 * Is `file` one of `allowedTypes`?
 *
 * Exact MIME match always. With `allowExtensionFallback`, a browser-unlabelled
 * file may also match on its extension — but only against the extensions the
 * ALLOWED types are already known by, so this never accepts a kind the caller
 * did not ask for (a PDF-only field still refuses an unlabelled `.mp4`).
 *
 * The fallback is opt-in because it is a client-side gate: enabling it where
 * the server still requires an exact MIME just moves the rejection later and
 * makes the message worse.
 */
export function isAllowedFileType(
  file: Pick<File, 'name' | 'type'>,
  allowedTypes: string[],
  allowExtensionFallback = false,
): boolean {
  const mimeType = file.type.trim().toLowerCase();
  if (allowedTypes.includes(file.type) || allowedTypes.includes(mimeType)) {
    return true;
  }

  if (!allowExtensionFallback) return false;
  if (!GENERIC_BINARY_MIME_TYPES.includes(mimeType)) return false;

  const fileName = file.name.trim().toLowerCase();
  return extensionsForMimeTypes(allowedTypes).some((extension) =>
    fileName.endsWith(extension),
  );
}

/**
 * Validates a file against the specified options
 */
export function validateFile(
  file: File,
  options: FileValidationOptions,
): FileValidationResult {
  const { allowedTypes, maxFileSize, allowExtensionFallback } = options;

  // Exact MIME match, plus — only for callers that opted in — the extension
  // fallback for a browser-unlabelled file (BAL-747). Every consumer still
  // re-validates server-side.
  if (!isAllowedFileType(file, allowedTypes, allowExtensionFallback)) {
    const typeLabels = getFileTypeLabels(allowedTypes);
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${typeLabels}`,
    };
  }

  // Check file size
  if (file.size > maxFileSize) {
    const sizeLabel = formatFileSize(maxFileSize);
    return {
      valid: false,
      error: `File size exceeds ${sizeLabel}`,
    };
  }

  return { valid: true };
}

/**
 * Formats bytes into human-readable size
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Gets human-readable labels for MIME types
 */
export function getFileTypeLabels(mimeTypes: string[]): string {
  const labels: string[] = [];

  if (mimeTypes.some((t) => t.startsWith('image/'))) {
    labels.push('images');
  }
  if (mimeTypes.some((t) => t.startsWith('video/'))) {
    labels.push('videos');
  }
  if (mimeTypes.includes('application/pdf')) {
    labels.push('PDFs');
  }

  return labels.join(', ');
}

/**
 * Gets the file type category based on MIME type
 */
export function getFileCategory(
  mimeType: string,
): 'image' | 'video' | 'document' | 'unknown' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'document';
  return 'unknown';
}

/**
 * Extracts filename without extension
 */
export function getFilenameWithoutExtension(filename: string): string {
  return filename.replace(/\.[^/.]+$/, '');
}
